'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildQuerySummary } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useMemo, useRef } from 'react';
import { useEngine } from '../EngineContext';
import { QuerySummarySkeleton } from '../Skeleton/QuerySummarySkeleton';
import { hasSearchQuery } from '../hooks/hasSearchQuery';
import { QuerySummaryProps } from './QuerySummary.types';

const TITLE_KEY = 'search.page-title-prefix';
const RESULTS_LABEL_KEY = 'search.results-count-label';
const RESULTS_OF_KEY = 'search.results-count-of';
const TITLE_PLACEHOLDER = '{{search}}';
const DEFAULT_TITLE_TEMPLATE = `Search results for "${TITLE_PLACEHOLDER}"`;

/**
 * Read the `q` parameter from the URL hash fragment as a fallback when the
 * Coveo QuerySummary controller does not expose `state.query` (this can
 * happen on deep-link navigations where the URL manager restores search
 * parameters but the controller state is not yet updated).
 */
function getQueryFromHash(): string {
  if (typeof window === 'undefined') return '';
  try {
    return new URLSearchParams(window.location.hash.slice(1)).get('q') ?? '';
  } catch {
    return '';
  }
}

export const QuerySummary = ({
  variant = 'full',
  className,
  testId = 'query-summary-count',
}: QuerySummaryProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const eng = useEngine();
  const controller = useMemo(() => buildQuerySummary(eng), [eng]);
  const state = useController(controller);
  const hasSearched = useRef(false);

  if (state.firstSearchExecuted) {
    hasSearched.current = true;
  }

  if (!state.hasResults && hasSearched.current) {
    return null;
  }

  // Show skeleton only during initial load, before the first search
  if (!state.firstSearchExecuted) {
    // Title variant only renders when there's a search query — skip skeleton
    // if no q= in the URL hash to avoid a layout shift when it disappears
    if (variant === 'title' && !hasSearchQuery()) return null;
    return <QuerySummarySkeleton variant={variant} className={className} />;
  }

  if (!state.hasResults) {
    return null;
  }

  const searchQuery = state.query || getQueryFromHash();
  const resultsLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    RESULTS_LABEL_KEY,
    'Results'
  );
  const resultsOf = getMicrocopyValue(
    searchLabelsMicrocopy,
    RESULTS_OF_KEY,
    'of'
  );

  if (variant === 'count') {
    return (
      <p className={className} data-test={testId}>
        {resultsLabel}{' '}
        <strong>
          {state.firstResult} – {state.lastResult}
        </strong>{' '}
        {resultsOf} <strong>{state.total}</strong>
      </p>
    );
  }

  const buildTitleElement = () => {
    if (!searchQuery) return null;
    const raw = getMicrocopyValue(
      searchLabelsMicrocopy,
      TITLE_KEY,
      DEFAULT_TITLE_TEMPLATE
    );
    const template = raw.includes(TITLE_PLACEHOLDER)
      ? raw
      : DEFAULT_TITLE_TEMPLATE;
    const placeholderIndex = template.indexOf(TITLE_PLACEHOLDER);
    const prefix = template.slice(0, placeholderIndex);
    const suffix = template.slice(placeholderIndex + TITLE_PLACEHOLDER.length);
    return (
      <>
        <span className="font-normal">{prefix}</span>
        <strong>{searchQuery}</strong>
        <span className="font-normal">{suffix}</span>
      </>
    );
  };

  const titleElement = buildTitleElement();

  if (variant === 'title') {
    if (!titleElement) return null;
    return (
      <h1 className={className} data-test="search-page-title">
        {titleElement}
      </h1>
    );
  }

  // Full variant (default) — used on mobile
  return (
    <div className={className} data-test="query-summary">
      {titleElement && (
        <h1 className="header-large mb-4 lg:mb-6" data-test="search-page-title">
          {titleElement}
        </h1>
      )}
      <p className="body-small text-grey-600" data-test={testId}>
        {resultsLabel}{' '}
        <strong>
          {state.firstResult} – {state.lastResult}
        </strong>{' '}
        {resultsOf} <strong>{state.total}</strong>
      </p>
    </div>
  );
};
